import React, { memo, useCallback, useMemo } from "react";
import { CustomTicketV2 } from "@markit/common.types";
import { useTheme } from "../../hooks/useTheme";
import { ContainerButton } from "../Buttons/ContainerButton";
import "../../css/FullEventSubComponents/TicketQuantityBox.css";

type TicketQuantityBoxProps = {
  selectedCustomTickets: CustomTicketV2[];
  ticketQuantity: number;
  setTicketQuantity: (ticketQuantity: number) => void;
  customTicket: CustomTicketV2;
  currTickets: number;
  disabled?: boolean;
};

export const TicketQuantityBox = memo(function TicketQuantityBoxFn(
  props: TicketQuantityBoxProps
) {
  const {
    selectedCustomTickets,
    ticketQuantity,
    setTicketQuantity,
    customTicket,
    currTickets,
    disabled,
  } = props;

  const { theme } = useTheme();

  const styles = {
    containerButton: {
      width: 24,
      height: 24,
      borderRadius: 8,
      padding: 4,
      outline: "none",
      WebkitTapHighlightColor: "transparent",
      userSelect: "none",
    } as React.CSSProperties,
  };

  const minQuantity = customTicket.minQuantity ?? 1;
  const maxEffective = Math.max(
    minQuantity,
    customTicket.maxQuantity - (currTickets || 0)
  );

  const handleClickContainer = useCallback((event: React.MouseEvent) => {
    event.stopPropagation();
  }, []);

  const handleAdd = useCallback(() => {
    if (disabled) return;
    setTicketQuantity(ticketQuantity === 0 ? minQuantity : ticketQuantity + 1);
  }, [disabled, minQuantity, setTicketQuantity, ticketQuantity]);

  const handleMinus = useCallback(() => {
    if (disabled) return;
    setTicketQuantity(ticketQuantity - 1);
  }, [disabled, setTicketQuantity, ticketQuantity]);

  const isPlusDisabled = useMemo(
    () => disabled || ticketQuantity >= maxEffective,
    [disabled, ticketQuantity, maxEffective]
  );
  // If the only selected ticket is the current ticket and the quantity is the min quantity, disable the minus button
  const isMinusDisabled = useMemo(
    () =>
      disabled ||
      (selectedCustomTickets.length === 1 &&
        selectedCustomTickets[0].id === customTicket.id &&
        ticketQuantity === customTicket.minQuantity),
    [
      disabled,
      selectedCustomTickets,
      customTicket.id,
      customTicket.minQuantity,
      ticketQuantity,
    ]
  );

  // When quantity is 0: single square plus button only
  if (ticketQuantity === 0) {
    return (
      <ContainerButton
        onPress={handleAdd}
        iconName="ion:add"
        iconSize={20}
        theme={theme}
        disabled={isPlusDisabled}
        containerStyles={styles.containerButton}
      />
    );
  }

  // When quantity > 0: [minus | number] slide in from right, plus stays in place
  return (
    <div
      className="AlignedRow"
      onClick={handleClickContainer}
      onMouseDown={handleClickContainer}
      style={{ gap: 10, overflow: "visible" }}
    >
      <div
        className="AlignedRow TicketQuantityBox-stepperEnter"
        style={{ gap: 10 }}
      >
        <ContainerButton
          onPress={handleMinus}
          iconName="mdi:minus"
          iconSize={18}
          theme={theme}
          disabled={isMinusDisabled}
          containerStyles={styles.containerButton}
        />
        <span className="sectionTitle" style={{ ...theme.PrimaryText }}>
          {ticketQuantity}
        </span>
      </div>
      <ContainerButton
        onPress={handleAdd}
        iconName="ion:add"
        iconSize={20}
        theme={theme}
        disabled={isPlusDisabled}
        containerStyles={styles.containerButton}
      />
    </div>
  );
});
